import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Typography } from '@mui/material';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { PAGE_CODES } from 'constants/pageCodes';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { selectAiProcesses } from 'store/system/selectors';

import {
  getLoanAiProcessOptions,
  getOptionName,
  getSelectedOption,
  isSameOption,
} from './LoanActionAiProcessSelect.utils';

/**
 * Picks one of the company's loan-scoped AI processes. Emits the process id. The process list is
 * loaded by the host page's loan-action dependencies.
 */
const LoanActionAiProcessSelect = ({
  dataTestId,
  name,
  onChange,
  processId = null,
  isDisabled = false,
  error = null,
}) => {
  const aiProcesses = useSelector(selectAiProcesses);
  const { isViewAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.AI_PROCESSES));

  const options = useMemo(
    () => getLoanAiProcessOptions({ aiProcesses, processId }),
    [aiProcesses, processId],
  );
  const selectedOption = getSelectedOption({ options, value: processId });

  const onProcessChange = useCallback((__, option) => onChange(option?.value ?? null), [onChange]);

  if (!isViewAllowed) {
    return (
      <Typography color={error ? 'error.main' : 'text.secondary'} variant="regular12">
        You need access to AI Processes to view or change the AI process for this action.
      </Typography>
    );
  }

  return (
    <AutocompleteCDS
      disableClearable
      required
      dataTestId={dataTestId}
      disabled={isDisabled}
      error={!!error}
      getOptionLabel={getOptionName}
      helperText={error ?? ''}
      isOptionEqualToValue={isSameOption}
      label="AI Process"
      name={name}
      options={options}
      value={selectedOption}
      onChange={onProcessChange}
    />
  );
};

LoanActionAiProcessSelect.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  isDisabled: PropTypes.bool,
  processId: PropTypes.string,
};

export default memo(LoanActionAiProcessSelect);
